import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";

const TONES = {
  teal: { wrap: "border-teal-500/30 bg-teal-500/5", glow: "from-teal-500/20", icon: "bg-teal-500/15 text-teal-500", btn: "bg-teal-500 hover:bg-teal-600" },
  indigo: { wrap: "border-indigo-500/30 bg-indigo-500/5", glow: "from-indigo-500/20", icon: "bg-indigo-500/15 text-indigo-400", btn: "bg-indigo-500 hover:bg-indigo-600" },
  emerald: { wrap: "border-emerald-500/30 bg-emerald-500/5", glow: "from-emerald-500/20", icon: "bg-emerald-500/15 text-emerald-500", btn: "bg-emerald-500 hover:bg-emerald-600" },
};

function Action({ a, tone, primary }) {
  const t = TONES[tone] || TONES.teal;
  const cls = primary
    ? `inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-semibold transition ${t.btn}`
    : "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition";
  const inner = <>{a.label} <ArrowRight className="h-4 w-4" /></>;
  return a.to
    ? <Link to={a.to} data-testid={a.testid} className={cls}>{inner}</Link>
    : <button onClick={a.onClick} data-testid={a.testid} className={cls}>{inner}</button>;
}

export default function GuidedEmpty({ icon: Icon, eyebrow, title, subtitle, primary, secondary = [], tips = [], tone = "teal", note, testid }) {
  const t = TONES[tone] || TONES.teal;
  return (
    <div className={`relative overflow-hidden p-8 sm:p-10 rounded-2xl border text-center ${t.wrap}`} data-testid={testid}>
      <div className={`absolute -top-20 left-1/2 -translate-x-1/2 h-64 w-64 rounded-full bg-gradient-to-b ${t.glow} to-transparent blur-3xl`} />
      <div className="relative max-w-lg mx-auto">
        <div className={`h-14 w-14 rounded-2xl flex items-center justify-center mx-auto ${t.icon}`}><Icon className="h-7 w-7" /></div>
        {eyebrow && <div className="mt-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">{eyebrow}</div>}
        <h2 className="font-display text-2xl font-extrabold tracking-tight mt-2">{title}</h2>
        <p className="text-sm text-muted-foreground mt-2">{subtitle}</p>

        {tips.length > 0 && (
          <ul className="mt-5 space-y-2 text-left inline-block">
            {tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${t.icon}`} />
                <span className="text-muted-foreground">{tip}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
          {primary && <Action a={primary} tone={tone} primary />}
          {secondary.map((s, i) => <Action key={i} a={s} tone={tone} />)}
        </div>

        {note && (
          <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> {note}
          </div>
        )}
      </div>
    </div>
  );
}
